import { PortData } from "../../wizard/steps/1-ends/end-step-circuit-dwdm.model";
import { TransponderPort } from "../../wizard/steps/2-transponders/transponders-step.model";
import { CircuitElementState, TransponderElement, TransponderMultirate } from "../models/diagram.models";
import { DwdmDiagramConverterHelper } from "./dwdm-diagram-converter-helper";
import { TranspondersConverterData } from "./dwdm-diagram-converter.models";

/**
 * Classe abstrata para realizar a conversão de @see TranspondersConverterData em @see TransponderElement.
 *
 * Essa classe contém todos os comportamentos comuns entre todas as janelas.
 * Comportamentos específicos da tela de Diagnósticos ou da Wizard de criação devem ser
 * implementados nas classes concretas.
 */
export abstract class DwdmDiagramAbstractConverterTransponders {

    /**
     * Mapa que armazena a relação de cada porta com a posição do transponder no diagrama.
     * FIXME US-36839
     */
    protected readonly PORT_BY_INDEX_MAP = {
        1: "four-hundred-g-ethernet 1/2/1",
        2: "four-hundred-g-ethernet 1/2/2",
        3: "four-hundred-g-ethernet 1/3/1",
        4: "four-hundred-g-ethernet 1/3/2"
    };

    protected convertData(transpondersData: TranspondersConverterData,
        customElementsConverter: (element: TransponderElement, portData: PortData) => void): TransponderElement[] {

        if (this.shouldBuildDefaultTransponders(transpondersData)) {
            return this.buildDefaultTransponders(transpondersData);
        }

        return this.buildConfiguredTransponders(transpondersData, customElementsConverter);
    }

    protected abstract shouldBuildDefaultTransponders(transpondersData: TranspondersConverterData): boolean;

    protected abstract buildDefaultTransponders(transpondersData: TranspondersConverterData): TransponderElement[];

    protected createTransponder(state: CircuitElementState, multirate: TransponderMultirate): TransponderElement {
        return {
            id: DwdmDiagramConverterHelper.createId(),
            state,
            multirate
        };
    }

    /**
     * Popula os transponders configurados e pre-configurados.
     */
    private buildConfiguredTransponders(transpondersData: TranspondersConverterData,
        customElementsConverter: (element: TransponderElement, portData: PortData) => void): TransponderElement[] {

        const transponders = [];
        const transponderPorts = transpondersData.transponders;
        const ports = transpondersData.ports;

        for (const portByIndex of Object.values(this.PORT_BY_INDEX_MAP)) {
            const foundPort = ports.find(port => port.portName === portByIndex);

            const { state, multirate } = this.getTransponderData(foundPort, transponderPorts);
            const transponderElement = this.createTransponder(state, multirate);

            customElementsConverter(transponderElement, foundPort);

            transponders.push(transponderElement);
        }

        return transponders;
    }

    private getTransponderData(foundPort: PortData, transponderPorts: TransponderPort[]) {
        let state: CircuitElementState;
        let multirate = TransponderMultirate.MULTIRATE_4X_100;

        if (foundPort) {
            // Quando a porta é encontrada, o estado do transponder pode ser configurado ou pré-configurado.
            // Isso é definido pela presença dos dados do step de transponders (transponderPorts), o que indica que
            // estamos neste step ou já passamos por ele. Assim, se ainda estamos no step de ends, o estado
            // é pré-configurado e os transceivers não são visíveis, mas se já passamos deste step, o estado
            // passa para configurado e os transceivers refletem o multirate configurado.
            multirate = this.getMultirate(foundPort, transponderPorts);
            state = transponderPorts ? CircuitElementState.CONFIGURED : CircuitElementState.PRE_CONFIGURED;
        } else {
            // Quando há slots configurados, mas a porta não é encontrada, o transponder pode assumir o estado
            // inicial (cinza) ou não configurado (azul claro). Isso é definido pela presença dos dados do step de
            // transponders (transponderPorts), o que indica que estamos neste step ou já passamos por ele. Dessa forma,
            // se estamos no step de ends, o estado será inicial, mas se estamos em qualquer outro step, o estado será
            // não configurado. Neste caso, o multirate sempre será o default.
            state = transponderPorts ? CircuitElementState.INITIAL : CircuitElementState.NOT_CONFIGURED;
        }

        return { state, multirate };
    }

    /**
     * O multirate setado em @see TransponderPort reflete o mostrado na tela para o usuário, por isso foi preciso verificar
     * pelo primeiro caracter do texto para identificar o @see TransponderMultirate adequado. O @see TransponderMultirate
     * define o número de transceivers que aparecerão em cada transponder do diagrama.
     */
    private getMultirate(port: PortData, transponderPorts: TransponderPort[]): TransponderMultirate {
        const possibleMultirates = ["1", "2", "3", "4"];
        const multirateValue = transponderPorts?.find(transponder => transponder.portName === port.portName)?.multirate;

        if (multirateValue && possibleMultirates.includes(multirateValue[0])) {
            return TransponderMultirate[`MULTIRATE_${multirateValue[0]}X_100`];
        }

        return TransponderMultirate.MULTIRATE_4X_100;
    }
}
